import { connectivity } from './connectivity';
import { canKeep, keepCopy, readCopy } from './storedReads';

/**
 * One fetch wrapper for the platform's API: same-origin cookies, JSON in and
 * out, and the CSRF token the platform puts in the XSRF-TOKEN cookie echoed
 * back in the X-XSRF-TOKEN header on every mutating call.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
  ) {
    super(`${status} ${code}`);
  }

  /**
   * What the platform said went wrong, when it said anything beyond the code.
   *
   * `not_possible` is every `IllegalStateException` the platform throws, so its
   * Catalan sentence has to cover all of them and says almost nothing. The one
   * that mattered — "generated definition invalid: [area size]", which meant no
   * mission could be generated at all — reached the browser in `detail` and was
   * thrown away. A screen that shows an error should show this after it.
   */
  get detail(): string | null {
    const body = this.body;
    if (body && typeof body === 'object' && 'detail' in body) {
      const value = (body as { detail?: unknown }).detail;
      return typeof value === 'string' && value.trim() && value !== 'null' ? value : null;
    }
    return null;
  }
}

/**
 * The request never reached the platform: the device has no connection, or
 * nothing answered. A write that ends here was not sent and is not kept to
 * send later (roadmap item app-installable: nothing is queued offline).
 */
export class OfflineError extends Error {
  constructor(cause?: unknown) {
    super('offline');
    this.cause = cause;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/** A proxy's own answer for a platform that is not behind it: a gateway status, and not the platform's JSON. */
function gateway(status: number, text: string): boolean {
  if (status < 502 || status > 504) return false;
  try {
    return JSON.parse(text) === null;
  } catch {
    return true;
  }
}

interface Answer {
  response: Response;
  text: string;
}

export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; cache?: RequestCache } = {},
  fetchImpl: typeof fetch = fetch,
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && method !== 'HEAD') {
    const token = readCookie('XSRF-TOKEN');
    if (token) headers['X-XSRF-TOKEN'] = token;
  }
  const send = async (): Promise<Answer> => {
    let response: Response;
    try {
      response = await fetchImpl(path, {
        method,
        headers,
        credentials: 'same-origin',
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
        // Only when a caller asks: a page that must never read a stored copy (a terrain's survey).
        ...(init.cache ? { cache: init.cache } : {}),
      });
    } catch (err) {
      throw new OfflineError(err);
    }
    return { response, text: await response.text() };
  };

  // A read may be answered from the copy this device kept of its last answer
  // (`storedReads.ts`) when the platform cannot answer it: the request failed
  // on the network, or a proxy answered with its gateway page. Never a write,
  // and never a read that asked for no stored copy. A slow answer is waited
  // for: a page that takes long is not a page with no connection, and showing
  // a copy then said "no connection" on a desktop whose network was fine
  // (web card 226, 2026-10-19). The page shows its loading shapes meanwhile.
  const keepable = method === 'GET' && init.cache !== 'no-store' && canKeep();
  let answer: Answer;
  try {
    answer = await send();
  } catch (err) {
    const copy = keepable ? await readCopy(path) : null;
    if (copy) {
      connectivity.servedStored(copy.storedAt);
      return JSON.parse(copy.text) as T;
    }
    connectivity.unreachable();
    throw err;
  }
  if (keepable && gateway(answer.response.status, answer.text)) {
    const copy = await readCopy(path);
    if (copy) {
      connectivity.servedStored(copy.storedAt);
      return JSON.parse(copy.text) as T;
    }
  }

  const { response, text } = answer;
  let parsed: unknown;
  let json = true;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch (err) {
    json = false;
    if (response.ok) throw err;
    parsed = text;
  }
  // Every answer says whether the platform is there (`connectivity.ts`): the
  // shell's notice reads it, so no page needs code of its own to say so. A
  // gateway status whose body is not the platform's JSON is the proxy's own:
  // the platform behind it is not there. One the platform wrote (a 503 for a
  // panel it cannot reach) is an answer.
  if (gateway(response.status, text)) connectivity.unreachable();
  else connectivity.answered();
  if (!response.ok) {
    const code =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `http_${response.status}`;
    throw new ApiError(response.status, code, parsed);
  }
  if (keepable && json) void keepCopy(path, text);
  return parsed as T;
}
